import { Button } from '@components/Button';
import { Option } from '@components/Option';
import { Select } from '@components/Select';
import { Label } from '@components/styled/Label.styled';
import { Title } from '@components/styled/Title.styled';
import { AutocompleteValue, TextAutocompleteInput } from '@components/TextAutocompleteInput';
import { TextInput } from '@components/TextInput';
import { getAddressSuggestions } from '@lib/api/smartyStreets';
import { SmartyStreetsSuggestion } from '@models/SmartyStreetsSuggestion';
import { MapPinIcon, VacasaLogo } from '@src/assets';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Chip } from './Chip';
import * as styled from './styled/AddressSearch.styled';
import { Header } from './styled/Header.styled';
import { TitleGroup } from './styled/TitleGroup.styled';

export interface Address {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
}

export interface AddressSearchProps {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    onAddress: (data: Address) => void;
}

export const AddressSearch: React.FC<AddressSearchProps> = ({
    street,
    city,
    state,
    zip,
    country,
    onAddress,
}) => {
    const formMethods = useForm({
        reValidateMode: 'onSubmit',
        defaultValues: useMemo(
            () => ({
                street: street || '',
                city: city || '',
                state: state || UsStates[0],
                zip: zip || '',
                country: country || 'USA',
            }),
            [],
        ),
    });

    useEffect(() => {
        formMethods.reset({
            street,
            city,
            state: state || UsStates[0],
            zip,
            country: country || 'USA',
        });
    }, [street, city, state, zip, country]);

    const handleSubmit = (data: any) => {
        onAddress(data);
    };

    const handleAddressSuggestions = useCallback(async (address: string, signal?: AbortSignal) => {
        if (!address || !address.trim()) return [];

        const res = await getAddressSuggestions(address.trim(), signal);
        if (200 <= res.status && res.status < 300) {
            return res.data.suggestions.map((x: SmartyStreetsSuggestion) => ({
                display: `${x.street_line} ${x.city}, ${x.state}, ${x.zipcode}`,
                value: x,
            }));
        } else {
            return [];
        }
    }, []);

    const handleAutocompleteClick = (clicked: AutocompleteValue<SmartyStreetsSuggestion>) => {
        const { street_line, city, state, zipcode } = clicked.value;
        handleSubmit({
            street: street_line,
            city: city,
            state: state,
            zip: zipcode,
            country: 'USA',
        });
    };

    return (
        <styled.AddressSearch>
            <styled.EstimatorApp>
                <VacasaLogo />
                <Header>
                    <Title>Real Estate Projection App</Title>
                </Header>
            </styled.EstimatorApp>
            <styled.Container>
                <TitleGroup>
                    <MapPinIcon />
                    <Title>Vacation home address</Title>
                    <Chip label="all fields required" />
                </TitleGroup>

                <styled.HomeDetails>
                    <FormProvider {...formMethods}>
                        <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
                            <styled.RowWrapper>
                                <styled.Wrapper>
                                    <Label>Street</Label>
                                    <TextAutocompleteInput
                                        name="street"
                                        width={645}
                                        autocomplete={handleAddressSuggestions}
                                        onAutocomplete={handleAutocompleteClick}
                                    />
                                </styled.Wrapper>
                            </styled.RowWrapper>
                            <styled.RowWrapper>
                                <styled.Wrapper>
                                    <Label>City</Label>
                                    <TextInput name="city" width={400} />
                                </styled.Wrapper>
                                <styled.Wrapper>
                                    <Label>State</Label>
                                    <Select name="state">
                                        {UsStates.map((state) => (
                                            <Option key={state} value={state}>
                                                {state}
                                            </Option>
                                        ))}
                                    </Select>
                                </styled.Wrapper>
                            </styled.RowWrapper>
                            <styled.RowWrapper>
                                <styled.Wrapper>
                                    <Label>Zip</Label>
                                    <TextInput name="zip" />
                                </styled.Wrapper>
                            </styled.RowWrapper>
                            <Button
                                className="submit"
                                onClick={() => handleSubmit(formMethods.getValues())}
                            >
                                Submit
                            </Button>
                        </form>
                    </FormProvider>
                </styled.HomeDetails>
            </styled.Container>
        </styled.AddressSearch>
    );
};

const UsStates = [
    'AL',
    'AK',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY',
];
